import {Select, Text} from '@mantine/core';
import React from 'react';

import {MappableLayer, ScratchPlateColumnMapping} from 'plates/dao/ScratchPlateDAO';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

// A source column is identified by (layerName, columnName). Mantine's Select
// works with a single string value per option, so we encode the pair into one
// opaque token and decode it on selection. The unit separator keeps the token
// unambiguous even if a layer or column name contains other punctuation.
const TOKEN_SEPARATOR = '\u001f';

function encodeSource(layerName: string, columnName: string): string {
  return `${layerName}${TOKEN_SEPARATOR}${columnName}`;
}

function decodeSource(token: string): {layerName: string; columnName: string} {
  const [layerName, columnName] = token.split(TOKEN_SEPARATOR);
  return {layerName, columnName};
}

export interface ScratchPlateColumnMappingTableProps {
  /** Container-schema fields that can be set (the mapping destinations). */
  schemaFields: Array<SchemaField>;
  /** Source columns, grouped by scratch-plate layer. */
  layers: Array<MappableLayer>;
  mappings: Array<ScratchPlateColumnMapping>;
  setMappings: (mappings: Array<ScratchPlateColumnMapping>) => void;
}

function buildSelectData(layers: Array<MappableLayer>): Array<{value: string; label: string; group: string}> {
  return layers.flatMap((layer) =>
    layer.columns.map((column) => ({
      value: encodeSource(layer.layerName, column.systemName),
      label: column.displayName,
      group: layer.layerName,
    }))
  );
}

/**
 * Destination-centric column mapping, mirroring the "map to results" UX: one
 * row per container-schema field, each with a dropdown of scratch-plate source
 * columns grouped by layer. Required fields are marked with an asterisk.
 *
 * Source columns are layer-qualified so that identical column names in
 * different layers stay distinct.
 */
export default function ScratchPlateColumnMappingTable({
  schemaFields,
  layers,
  mappings,
  setMappings,
}: ScratchPlateColumnMappingTableProps): JSX.Element {
  const selectData = buildSelectData(layers);

  const selectedSourceByField = new Map<string, string>(
    mappings.map((m) => [m.schemaFieldName, encodeSource(m.layerName, m.columnName)])
  );

  function handleChange(schemaFieldName: string, token: string | null): void {
    const withoutField = mappings.filter((m) => m.schemaFieldName !== schemaFieldName);
    if (token == null) {
      setMappings(withoutField);
      return;
    }
    const {layerName, columnName} = decodeSource(token);
    setMappings([...withoutField, {layerName, columnName, schemaFieldName}]);
  }

  if (schemaFields.length === 0) {
    return <Text c="dimmed">This schema does not have any fields to map.</Text>;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Plate column</th>
          <th>Scratch plate column</th>
        </tr>
      </thead>
      <tbody>
        {schemaFields.map((schemaField) => (
          <tr key={schemaField.systemName}>
            <td>
              {schemaField.displayName}
              {schemaField.isRequired && (
                <Text span={true} c="red.6">
                  {' '}
                  *
                </Text>
              )}
            </td>
            <td>
              <Select
                data={selectData}
                value={selectedSourceByField.get(schemaField.systemName) ?? null}
                onChange={(value) => {
                  handleChange(schemaField.systemName, value);
                }}
                placeholder="None"
                clearable={!schemaField.isRequired}
                searchable={true}
                nothingFound="No columns"
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
